<!-- SiteView.vue -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSites } from '@/composables/useSites'
import { useFiles, type File } from '@/composables/useFiles'
import MapComponent from '@/components/MapComponent.vue'
import { useMapStore } from '@/stores/mapStore'
import { fromLonLat } from 'ol/proj'
import Polygon from 'ol/geom/Polygon.js'
import Feature from 'ol/Feature.js'
import VectorSource from 'ol/source/Vector.js'
import VectorLayer from 'ol/layer/Vector.js'
import Style from 'ol/style/Style.js'
import Fill from 'ol/style/Fill.js'
import Stroke from 'ol/style/Stroke.js'
import FileListVue from '@/components/FileList.vue'
import FalseColorComposite from '@/components/FalseColorComposite.vue'
import WebGLTileLayer from 'ol/layer/WebGLTile'
import GeoTIFFSource from 'ol/source/GeoTIFF'
import { useAuthStore } from '@/stores/auth'
import { useDrawer } from '@/composables/useDrawer'
import { toLonLat } from 'ol/proj'
import {transform} from 'ol/proj.js'
import {GeoTIFF, fromBlob} from 'geotiff'

const route = useRoute()
const router = useRouter()
const siteId = computed(() => route.params.site_id as string)

const goBackToSite = () => {
  if (siteId.value) {
    router.push(`/site/${siteId.value}/`)
  }
}

const { sites, fetchItems } = useSites()
const siteInfo = computed(() => {
  return sites.value.find((site) => site.name === siteId.value)
})

const { files, fetchFiles, fetchProjectFiles, projectFileList } = useFiles()
const mapStore = useMapStore()

// Track which layers correspond to which files
const fileLayers = ref<Map<string, any>>(new Map())
const falseColorComposite = ref<any>(null)
const fccMode = ref(false)

const toggleFccMode = () => {
  fccMode.value = !fccMode.value
}

onMounted(async () => {
  await fetchItems()
  await fetchFiles()
  await fetchProjectFiles(siteId.value)

  files.value = projectFileList.value
  console.log(files.value)

  // sort files alphabetically by name
  files.value.sort((a, b) => a.name.localeCompare(b.name))

  const mapContainer = mapStore.map

  if (!mapContainer) {
    throw new Error('Map not initialized')
  }
  const mapContainerView = mapContainer.getView()

  const setZoom = () => {
    mapContainerView.setZoom(10)
  }

  const setCenter = () => {
    if (siteInfo.value) {
      mapContainerView.setCenter(
        fromLonLat([(siteInfo.value as any).centre_lon, (siteInfo.value as any).centre_lat])
      )
    }
  }

  if (!siteInfo.value) {
    throw new Error('Site not found')
  }
  // Create a polygon feature for the site (square around geometry)
  // Geometry is [north, east, south, west] string, first split by comma, then map to float
  const geomString = (siteInfo.value as any).geometry
  // remove []
  const siteGeometry = geomString
    .slice(1, geomString.length - 1)
    .split(',')
    .map((coord: string) => parseFloat(coord))
  console.log('siteGeometry:', siteGeometry)
  const siteExtent = [
    [siteGeometry[3], siteGeometry[0]],
    [siteGeometry[3], siteGeometry[2]],
    [siteGeometry[1], siteGeometry[2]],
    [siteGeometry[1], siteGeometry[0]],
    [siteGeometry[3], siteGeometry[0]]
  ].map((coord) => fromLonLat(coord))
  const sitePolygon = new Polygon([siteExtent])
  const polyStyle = new Style({
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.0)'
    }),
    stroke: new Stroke({
      color: '#dddddd',
      width: 3,
      lineDash: [3, 1],
      lineCap: 'butt'
    })
  })

  // Zoom to the site extent
  const siteBounds = sitePolygon.getExtent()
  mapContainerView.fit(siteBounds, { size: mapContainer.getSize() })
  console.log('Zoom level:', mapContainerView.getZoom())
  console.log('Should be fitting to site bounds:', siteBounds)
  // Convert extent to Coordinates
  const sbCoords = [
    [siteBounds[0], siteBounds[1]],
    [siteBounds[2], siteBounds[3]]
  ]
  //  In lat lon that is:
  const siteBoundsLonLat = sbCoords.map((coord) => toLonLat(coord))

  console.log('Should be fitting to site bounds:', siteBoundsLonLat)

  const siteFeature = new Feature(sitePolygon)
  // Create a vector source and layer
  const vectorSource = new VectorSource({
    features: [siteFeature]
  })
  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: polyStyle
  })
  // Add the vector layer to the map
  mapContainer.addLayer(vectorLayer)

  setZoom()
  setCenter()

  mapContainerView.fit(sitePolygon, { padding: [100, 100, 100, 100] })
})

const handleFileSelected = async (file: File) => {
  // If in FCC mode, assign file to FCC component instead of loading on map
  if (fccMode.value && falseColorComposite.value) {
    falseColorComposite.value.addFile(file)
    return
  }

  const mapContainer = mapStore.map
  if (!mapContainer) {
    console.error('Map container not initialized')
    return
  }

  console.log('File selected:', file.name)
  // Do something with the selected file
  const fileType = file.name.split('.').pop()
  if (fileType === 'tif' || fileType === 'tiff') {
    console.log('TIF file selected')
    const project = siteId.value
    const fileUrl = `/api/media/protected_image/${project}/${file.name}`

    try {
      const data = await fetch(fileUrl)
      const blob = await data.blob()
      console.log('Blob size:', blob.size)

      // Default min/max values
      let min = 0
      let max = 1

      // Set min/max based on file type
      if (file.name.includes('TRUE_COLOR')) {
        min = 0
        max = 255
        console.log('True color image')
      } else if (file.name.toLowerCase().includes('coherence')) {
        min = 0
        max = 1
        console.log('Coherence file')
      } else if (file.name.includes('VV')) {
        max = 5.5
        console.log('VH file')
      } else if (file.name.toLowerCase().includes('phase')) {
        min = -3.14
        max = 3.14
        console.log('Displacement file')
      } else if (file.name.includes('VV_MEAN')) {
        min = 60
        max = 120
        console.log('VV_MEAN file')
      }

      // Create GeoTIFF source with proper configuration
      const geoTiffSource = new GeoTIFFSource({
        sources: [{
          blob: blob,
          min: min,
          max: max,
          nodata: -999
        }],
        projection: 'EPSG:4326'
      })

      // Create and add the layer
      const imageLayer = new WebGLTileLayer({
        source: geoTiffSource,
        opacity: 1,
        visible: true
      })

      console.log('Adding image layer to map')
      mapContainer.addLayer(imageLayer)
      
      // Track this layer for this file
      fileLayers.value.set(file.name, imageLayer)

    } catch (err) {
      console.error('Error processing GeoTIFF:', err)
    }
  } else {
    console.log('File type not supported')
  }
}

const handleFileUnloaded = (file: File) => {
  const mapContainer = mapStore.map
  if (!mapContainer) {
    console.error('Map container not initialized')
    return
  }

  console.log('File unloaded:', file.name)
  
  // Find and remove the layer for this file
  const layer = fileLayers.value.get(file.name)
  if (layer) {
    mapContainer.removeLayer(layer)
    fileLayers.value.delete(file.name)
    console.log('Removed layer for file:', file.name)
  }
}

const handleCompositeCreated = (layer: WebGLTileLayer) => {
  const mapContainer = mapStore.map
  if (!mapContainer) {
    console.error('Map container not initialized')
    return
  }

  console.log('Adding false color composite layer to map')
  mapContainer.addLayer(layer)
  
  // Track this composite layer
  fileLayers.value.set('false_color_composite', layer)
}

const handleCompositeCleared = () => {
  const mapContainer = mapStore.map
  if (!mapContainer) {
    console.error('Map container not initialized')
    return
  }

  // Remove the false color composite layer if it exists
  const layer = fileLayers.value.get('false_color_composite')
  if (layer) {
    mapContainer.removeLayer(layer)
    fileLayers.value.delete('false_color_composite')
    console.log('Removed false color composite layer')
  }
}

const handleFccCancel = () => {
  fccMode.value = false
}

const clearLayers = () => {
  const mapContainer = mapStore.map
  if (!mapContainer) {
    console.error('Map container not initialized')
    return
  }

  // Get all layers
  const layers = mapContainer.getLayers()
  
  // Remove all layers except the base layer (first layer)
  while (layers.getLength() > 1) {
    layers.removeAt(1)
  }
}

const handleAddToFcc = (file: File) => {
  if (falseColorComposite.value) {
    const success = falseColorComposite.value.addFile(file)
    if (!success) {
      console.log('Could not assign file to FCC (all bands may be full or not TIFF):', file.name)
    }
  }
}

const handleToggleFcc = () => {
  fccMode.value = !fccMode.value
  if (!fccMode.value && falseColorComposite.value) {
    // Clear FCC when exiting mode
    falseColorComposite.value.clearAll()
  }
}
</script>

<template>
  <div class="flex">
    <Card class="w-1over3">
      <template #content>
        <div class="flex items-center gap-3 mb-2">
          <Button 
            @click="goBackToSite"
            icon="pi pi-arrow-left"
            severity="secondary"
            outlined
            size="small"
            class="flex-shrink-0"
            aria-label="Back to Site"
          />
          <h1 class="m-0">{{ siteId }} Data Viewer</h1>
        </div>
        <Divider />
        <FalseColorComposite 
          :siteName="siteId" 
          :isVisible="fccMode"
          @composite-created="handleCompositeCreated"
          @composite-cleared="handleCompositeCleared"
          @cancel="handleFccCancel"
          ref="falseColorComposite"
        />
        <Divider v-if="fccMode" />
        <FileListVue 
          :siteName="siteId" 
          :files="files" 
          :fccMode="fccMode"
          @file-click="handleFileSelected" 
          @file-unload="handleFileUnloaded"
          @add-to-fcc="handleAddToFcc"
          @toggle-fcc="handleToggleFcc"
        />
        <Button @click="clearLayers" class="mt-2">Clear Layers</Button>
      </template>
    </Card>
    <Divider layout="vertical" />
    <div class="w-2over3">
      <div id="site-info">{{ siteInfo ? '' : 'Loading...' }}</div>
      <MapComponent />
    </div>
  </div>
</template>

<style scoped>
.w-1over3 {
  /* width: 30%; */
  flex-grow: 1;
}
.w-2over3 {
  /* width: 60%;
   */
   flex-grow: 2;
}
.mt-2 {
  margin-top: 0.5rem;
}
</style>